import { getCompositeMapCanvas, getMeasureToolsCoords } from '@openlayers/ol-utils';
import { Cache } from '@shared/Cache';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { DrawingsDisplayOpenlayers } from '@shared/user-state/comments/DrawingsDisplayOpenlayers';
import { SavedStateCommentsPanel } from '@shared/user-state/comments/SavedStateCommentsPanel';
import { PageStateService } from '@shared/user-state/PageStateService';
import { CanvasUtils } from '@shared/utils/CanvasUtils';
import { isCrashProneMobile } from '@shared/utils/utils';
import { transform as olTransform } from 'ol/proj';
import * as React from 'react';
import { mapDrawing, mapRootStore, mapStateFacade, mapStation, mapTools, mapUI, mapWrapper } from '../map';
import { WindowManager } from '@shared/ui-react/FloatingWindow';
import { Notify } from '@shared/ui-native/Notify';
import { Events, EventsGlobal } from '@shared/Events';

export class Map2dPageStateHandler implements IPageStateHandler<IMapPageState> {
    version = 1;

    clearState(): void {
        mapTools.deactivateAllTools();
        mapStation.removeStationLine();
        mapWrapper.clickMarkerLayer.removeClickMarker();
        mapRootStore.setActiveAlignment(-1, 0);
        mapWrapper.featureLayers.alignments.setActiveAlignmentById(-1, 0);
        mapWrapper.stateful.restoreDefaultState();
        mapRootStore.vehiclesStore.stateful.restoreDefaultState();
        mapRootStore.marineStore.stateful.restoreDefaultState();

        if (mapUI.mapPanels.panels.models) mapUI.mapPanels.panels.models.stateful.restoreDefaultState();
        if (mapUI.mapPanels.panels.savedViews) mapUI.mapPanels.panels.savedViews.store.stateful.restoreDefaultState();
        if (mapUI.mapPanels.panels.logpoints) mapUI.mapPanels.panels.logpoints.genericComponent.stateful.restoreDefaultState();
        if (mapUI.mapPanels.panels.documents) mapUI.mapPanels.panels.documents.stateful.restoreDefaultState();
        if (mapUI.mapPanels.panels.images) mapUI.mapPanels.panels.images.stateful.restoreDefaultState();

        Cache.clear();
    }

    async getCurrentState(withThumbnail = true): Promise<IMapPageState | null> {
        // The map UI may not be mounted yet (or is being torn down) when the
        // live-state poller fires; there is no state to capture then.
        if (!mapUI?.mapPanels) return null;

        let thumbnail = '';
        if (withThumbnail) {
            const canvas = getCompositeMapCanvas(mapWrapper.getInstance());
            thumbnail = await CanvasUtils.toBase64(canvas, 250, 'jpeg', 0.6);
        }

        let windows = [];
        try {
            const csWindows = [...WindowManager.getByGroupId('cross-section'), ...WindowManager.getByGroupId('long-section')];
            windows = csWindows.map(w => w.getState()).filter(Boolean);
        } catch (e) {
            console.error(e);
        }

        const state: IMapPageState = {
            name: null,
            folderId: null,
            page: 'MAP',
            dataVersion: this.version,
            thumbnail,
            data: {
                panel: {
                    activePanel: mapUI.mapPanels.getActivePanelId(),
                    vehicles: kuura.hasPrivilege('VEHICLE_PAGE') ? mapRootStore.vehiclesStore.stateful.getCurrentState() : null,
                    marine: kuura.hasAisIntegrationEnabled() ? mapRootStore.marineStore.stateful.getCurrentState() : null,
                    models: mapUI.mapPanels.panels.models ? mapUI.mapPanels.panels.models.stateful.getCurrentState() : null,
                    savedViews: mapUI.mapPanels.panels.savedViews ? mapUI.mapPanels.panels.savedViews.store.stateful.getCurrentState() : null,
                    logpoints: mapUI.mapPanels.panels.logpoints ? mapUI.mapPanels.panels.logpoints.genericComponent?.stateful.getCurrentState() : null,
                    documents: mapUI.mapPanels.panels.documents ? mapUI.mapPanels.panels.documents.stateful.getCurrentState() : null,
                    images: mapUI.mapPanels.panels.images ? mapUI.mapPanels.panels.images.stateful.getCurrentState() : null,
                    mass: {},
                    schedule: {},
                },
                windows,
                activeAlignmentsId: mapRootStore.activeAlignment.modelId,
                activeAlignmentsIndex: mapRootStore.activeAlignment.index || 0,
                map: mapWrapper.stateful.getCurrentState(),
                featureCounts: {
                    models: mapWrapper.featureLayers.models.getFeaturesCount(),
                    bgMaps: 0,
                    logpoints: mapWrapper.featureLayers.logpoints.getFeaturesCount(),
                    images: mapRootStore.imagesStore.getFilteredIgnoreMapHidden().length,
                    documents: mapRootStore.documentsStore.getFilteredIgnoreMapHidden().length,
                },
            },
        };

        const measurements: { area?: any; distance?: any } = {};

        state.data.map.measurements = getMeasureToolsCoords(mapTools.distanceTool, mapTools.areaTool);

        return state;
    }

    async restoreState(state: IMapPageState, disableSafeguards = false) {
        // keep old state so they can restore it with button from saved view panel
        // if they load state from url, old state is the one from database, otherwise the state when panel was opened
        let oldState = null;
        if (utils.getUrlVars().state) {
            oldState = await PageStateService.liveState.fetch('MAP');
            // can be null, user might not have live state yet, because user opened project for the first time
            if (!oldState) {
                oldState = await this.getCurrentState(false);
            }
        } else {
            oldState = await this.getCurrentState(false);
        }

        oldState.liveState = true;

        console.log('[ map ] Restoring full page state, state:', state);
        if (!state || !state.data) {
            console.log('[ map ] Empty state, fallback to default state');
            this.restoreDefaultState();
            this.loadOnboardingState();
            return;
        }

        // map view
        if (state?.data?.map) {
            mapWrapper.stateful.restoreState(state.data.map);
            mapTools.restoreMeasurementsFromCoords(state.data.map.measurements);
            Events.trigger(EventsGlobal.MAP_MEASUREMENTS_LOADED);
        }

        // panels
        if (state?.data?.panel) {
            // clear data heavy items from live state
            if (state.liveState && !disableSafeguards) {
                if (mapUI.mapPanels.panels.logpoints && state.data.panel.logpoints) {
                    state.data.panel.logpoints.checkedFileIds = [];
                    state.data.panel.logpoints.checkedFolderIds = [];
                }
                if (mapUI.mapPanels.panels.models && state.data.panel.models) {
                    if (state.data.panel.models.fileIds?.length > 50 || isCrashProneMobile()) {
                        state.data.panel.models.fileIds = [];
                    }
                    if (state.data.panel.models.headUuids?.length > 50 || isCrashProneMobile()) {
                        state.data.panel.models.headUuids = [];
                    }
                    if (isCrashProneMobile()) {
                        state.data.panel.models.showDeviations = false;
                    }
                }
                if (state.data.panel.bgMaps && isCrashProneMobile()) {
                    state.data.panel.bgMaps.fileIds = [];
                    state.data.panel.bgMaps.headUuids = [];
                }
            }

            // floating windows
            WindowManager.closeAll();
            if (!state.liveState && state?.data?.windows) {
                state?.data?.windows.forEach(w => {
                    if (w.group === 'cross-section') {
                        mapTools.openCrossSectionWindow(null, null, null, null, w);
                    } else if (w.group === 'long-section') {
                        mapTools.openLongSectionWindow(null, null, w);
                    }
                });
            }

            if (utils.getUrlVars()?.state) {
                mapUI.mapPanels.activatePanelById('savedViews');
                mapStateFacade.store.setSelectedFolderId(state.folderId, state.id);
            } else {
                mapUI.mapPanels.activatePanelById(state.data.panel.activePanel as any);
            }
            if (kuura.hasPrivilege('VEHICLE_PAGE')) mapRootStore.vehiclesStore.stateful.restoreState(state.data.panel.vehicles, state.liveState);
            if (kuura.hasAisIntegrationEnabled()) mapRootStore.marineStore.stateful.restoreState(state.data.panel.marine);
            if (mapUI.mapPanels.panels.documents) mapUI.mapPanels.panels.documents.stateful.restoreState(state.data.panel.documents);
            if (mapUI.mapPanels.panels.images) mapUI.mapPanels.panels.images.stateful.restoreState(state.data.panel.images);
            if (mapUI.mapPanels.panels.logpoints) mapUI.mapPanels.panels.logpoints.genericComponent.stateful.restoreState(state.data.panel.logpoints, state.liveState);
            if (mapUI.mapPanels.panels.savedViews) mapUI.mapPanels.panels.savedViews.store.stateful.restoreState(state.data.panel.savedViews, state.liveState);

            if (state.data.panel.models && state.data.panel.bgMaps) {
                // handle legacy background maps panel
                state.data.panel.models.fileIds = Array.from(new Set([...(state.data.panel.models.fileIds ?? []), ...(state.data.panel.bgMaps.fileIds ?? [])]));
                state.data.panel.models.headUuids = Array.from(new Set([...(state.data.panel.models.headUuids ?? []), ...(state.data.panel.bgMaps.headUuids ?? [])]));
                state.data.panel.models.rasterizedModelIds = state.data.panel.bgMaps.rasterizedModelId
                    ? [state.data.panel.bgMaps.rasterizedModelId]
                    : state.data.panel.bgMaps.rasterizedModelIds;
            }
            if (mapUI.mapPanels.panels.models) mapUI.mapPanels.panels.models.stateful.restoreState(state.data.panel.models);
        }

        setTimeout(async () => {
            if (state.data.activeAlignmentsId < 0 && state.liveState) {
                state.data.activeAlignmentsId = kuura.getProjectAlignment() ? parseInt(kuura.getProjectAlignment().id) : -1;
            }
            // active alignment - models.state.restoreState(), will clear active alignment so make sure we execute after it
            mapRootStore.setActiveAlignment(state.data.activeAlignmentsId ? state.data.activeAlignmentsId : -1, state.data.activeAlignmentsIndex || 0);

            if (state.visibility !== 'HIDDEN' && !state.liveState) {
                mapUI.mapPanels.panels.savedViews.store.focusItemId = state.id;
                const commentsPanel: HTMLElement = document.querySelector('#screenshot-drawings-panel');

                ReactUtils.safeRender(
                    React.createElement(SavedStateCommentsPanel, {
                        page: 'MAP2D',
                        pageState: state,
                        canvasDrawing: mapDrawing,
                        drawingsDisplay: new DrawingsDisplayOpenlayers(mapWrapper, state.data.map?.view?.zoom ?? state.data.drawingsZoom),
                        treeData: mapRootStore.treeData,
                        calculateDrawingLatLon: pixelPos => {
                            const ne = mapWrapper.getCoordinateFromPixel(pixelPos);
                            const lonLat = olTransform(ne, 'EPSG:3857', 'EPSG:4326');
                            return lonLat.reverse() as [number, number];
                        },
                        onClose: (restorePreviousState = false) => {
                            ReactUtils.safeUnmountComponentAtNode(commentsPanel);
                            mapTools.clearMeasurements();
                            mapUI.mapPanels.panels.savedViews.store.focusItemId = null;

                            if (restorePreviousState) {
                                if (oldState) {
                                    this.restoreState(oldState, true);
                                }
                            }
                        },
                        onResetView: state => {
                            // 2d map drawings are only valid for single zoom level
                            const zoom = state.data.map?.view?.zoom ?? state.data.drawingsZoom;
                            if (typeof zoom !== 'number') return;
                            mapWrapper.setZoom(zoom);
                        },
                        onDownloadSavedView: state => {
                            mapUI.mapPanels.onDownloadSavedView(state);
                        },
                        onShowInPanel: state => {
                            mapUI.mapPanels.activatePanelById('savedViews');
                            mapUI.mapPanels.panels.savedViews.store.setSelectedFolderId(state.folderId, state.id);
                        },
                        onUpdateState: async state => {
                            if (confirm(__('js_saved_views.update_with_current_map_state') + ' ?')) {
                                const currentState = await this.getCurrentState(true);
                                state.data = currentState.data;
                                state.dataVersion = currentState.dataVersion;
                                state.thumbnail = currentState.thumbnail;
                                try {
                                    await PageStateService.update(state.uuid, { ...state });
                                    mapUI.mapPanels.panels.savedViews.store.refresh();
                                    Notify.success();
                                } catch {
                                    Notify.error();
                                }
                            }
                        },
                        getMapAngle: () => mapWrapper.getInstance().getView().getRotation(),
                        getMapZoom: () => mapWrapper.getZoom(),
                    }),
                    commentsPanel
                );
            }
        }, 250);
    }

    restoreDefaultState() {
        mapUI.mapPanels.activatePanelById('models');
        mapWrapper.stateful.restoreDefaultState();
    }

    loadOnboardingState() {
        mapUI.mapPanels.activatePanelById('models');
        if (kuura.getProjectWorksiteMapModel()) {
            mapUI.mapPanels.panels.models.stateful.restoreState({
                fileIds: [parseInt(kuura.getProjectWorksiteMapModel().id)],
            });
        }
    }
}
